'use client';
import React, { useState, useEffect } from 'react';
import NextLink from 'next/link';
import { ExpandMoreRounded, LogoutRounded, PersonRounded } from '@mui/icons-material';
import {
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Typography,
  Skeleton,
} from '@mui/material';
import { headerLinks } from '@/shared/utils';
import { useCurrentPath } from '@/shared/hooks';
import { useServerAuth } from '@/auth';
import { useRouter } from 'next/navigation';
import HeaderProfileImage from '../profile-image';
import { useProfile } from '@/profile';

const SkeletonLoader = () => {
  return (
    <div className="flex ml-auto">
      <Skeleton variant="text" width={200} height={40} />
    </div>
  );
};

const DesktopSubHeader: React.FC<{ showLinks: boolean }> = ({ showLinks }) => {
  const router = useRouter();
  const { user, logout, hydrated } = useServerAuth();
  const { profile } = useProfile(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [, isCurrentPath] = useCurrentPath();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        handleClose();
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, anchorEl]);

  const handleLogout = async () => {
    if (logout) {
      logout().then(() => {
        handleClose();
      });
    }
  };

  const headerLinksMemonized = React.useMemo(
    () => headerLinks(user !== null, profile?.subscriber),
    [user, profile?.subscriber],
  );

  return hydrated ? (
    <>
      {showLinks && (
        <div className="flex gap-x-4">
          {headerLinksMemonized.map((link) => (
            <NextLink
              key={link.href}
              style={{ textDecoration: 'none' }}
              // target={link.label.toLowerCase().includes('community') ? '_blank' : '_self'}
              className="transition ease-in-out hover:text-primary-main"
              href={link.href}
            >
              <Typography
                sx={{
                  color: isCurrentPath(link.href) ? 'primary.main' : 'primary',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                }}
              >
                {link.label}
              </Typography>
            </NextLink>
          ))}
        </div>
      )}
      <div>
        {user ? (
          <div className="flex items-center gap-x-0 lg:gap-x-2">
            {profile?.name ? (
              <Typography variant="body-400" className="hidden lg:block">
                {profile?.name?.split(' ')?.[0] || ''}
              </Typography>
            ) : null}
            <HeaderProfileImage
              userEmail={user?.email}
              userProfilePicture={profile?.profilePicture || null}
            />
            <div>
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                className="text-white transition ease-in-out hover:text-primary-main"
                onClick={handleClick}
              >
                <ExpandMoreRounded />
              </IconButton>
              <Menu
                id="long-menu"
                MenuListProps={{ 'aria-labelledby': 'long-button' }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuList className="border rounded-lg shadow-none border-action-divider">
                  {user && (
                    <>
                      <MenuItem
                        className="gap-x-2 px-6 w-[260px] transition ease-in-out hover:text-primary-main"
                        onClick={() => {
                          handleClose();
                          router.push('/profilo/me');
                        }}
                      >
                        <PersonRounded />
                        <Typography variant="button-l">Profilo</Typography>
                      </MenuItem>
                      <Divider />
                    </>
                  )}
                  <MenuItem
                    onClick={handleLogout}
                    className="gap-x-2 px-6 w-[260px] transition ease-in-out hover:text-primary-main"
                  >
                    <LogoutRounded />
                    <Typography variant="button-l">Logout</Typography>
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>
          </div>
        ) : (
          <div className="flex gap-x-4">
            <NextLink href="/auth/login">
              <Button size="small" variant="outlined" color="primary">
                <Typography variant="button-m">Accedi</Typography>
              </Button>
            </NextLink>
            <NextLink href="/auth/registrazione">
              <Button size="small" variant="contained" color="primary">
                <Typography variant="button-m">Iscriviti</Typography>
              </Button>
            </NextLink>
          </div>
        )}
      </div>
    </>
  ) : (
    <SkeletonLoader />
  );
};

export default DesktopSubHeader;
